import { MediaElement } from 'editor/src/store/design/types';

import { getRectangleBoundingBox } from 'editor/src/util/reflectDesignData/utils';

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Share of the page a group that has to be scaled down is fitted into. */
const FIT_RATIO = 0.9;

function getElementBox(element: MediaElement): Box | undefined {
  if (element.type === 'line') {
    const half = element.strokeWidth / 2;
    return {
      left: Math.min(element.x1, element.x2) - half,
      right: Math.max(element.x1, element.x2) + half,
      top: Math.min(element.y1, element.y2) - half,
      bottom: Math.max(element.y1, element.y2) + half,
    };
  }
  if (element.type === 'rectangle' || 'x' in element) {
    const { x, y, width, height } = element as { x: number; y: number; width: number; height: number };
    const rotation = (element as { r?: number }).r ?? 0;
    const box = getRectangleBoundingBox(x, y, width, height, rotation);
    return { left: box.x, top: box.y, right: box.x + box.width, bottom: box.y + box.height };
  }
  return undefined;
}

function transformElement(element: MediaElement, map: (x: number, y: number) => [number, number], scale: number) {
  if (element.type === 'line') {
    const [x1, y1] = map(element.x1, element.y1);
    const [x2, y2] = map(element.x2, element.y2);
    return { ...element, x1, y1, x2, y2, strokeWidth: element.strokeWidth * scale };
  }

  const positioned = element as MediaElement & { x: number; y: number; width: number; height: number };
  const [x, y] = map(positioned.x, positioned.y);
  const next = { ...positioned, x, y, width: positioned.width * scale, height: positioned.height * scale };

  if (scale !== 1) {
    if (next.type === 'text') {
      // The cached glyph outline (custom fonts) was drawn at the old size.
      return { ...next, path: undefined, extra: { ...next.extra, fontSize: next.extra.fontSize * scale } };
    }
    if (next.type === 'rectangle') {
      return { ...next, strokeWidth: next.strokeWidth * scale };
    }
  }
  return next;
}

/**
 * Imported layers keep their position in mm from the design they were
 * exported from. Into a smaller (or differently shaped) product that can put
 * them off the page, where they are invisible. When the group already fits,
 * nothing moves — importing into the same product keeps exact positions.
 * Otherwise the group is centred on the page, and scaled down uniformly first
 * if it is larger than the page.
 */
function fitImportedElementsToPage(elements: MediaElement[], pageWidth: number, pageHeight: number): MediaElement[] {
  const boxes = elements.map(getElementBox).filter((box): box is Box => !!box);
  if (!boxes.length) {
    return elements;
  }

  const group: Box = {
    left: Math.min(...boxes.map((b) => b.left)),
    top: Math.min(...boxes.map((b) => b.top)),
    right: Math.max(...boxes.map((b) => b.right)),
    bottom: Math.max(...boxes.map((b) => b.bottom)),
  };
  if (group.left >= 0 && group.top >= 0 && group.right <= pageWidth && group.bottom <= pageHeight) {
    return elements;
  }

  const groupWidth = group.right - group.left;
  const groupHeight = group.bottom - group.top;
  const scale =
    groupWidth > pageWidth || groupHeight > pageHeight
      ? Math.min((pageWidth * FIT_RATIO) / groupWidth, (pageHeight * FIT_RATIO) / groupHeight)
      : 1;
  const offsetX = (pageWidth - groupWidth * scale) / 2;
  const offsetY = (pageHeight - groupHeight * scale) / 2;
  const map = (x: number, y: number): [number, number] => [
    offsetX + (x - group.left) * scale,
    offsetY + (y - group.top) * scale,
  ];

  return elements.map((element) => (getElementBox(element) ? transformElement(element, map, scale) : element));
}

export default fitImportedElementsToPage;
